import {Component, Input, OnInit, Optional, TemplateRef, ViewChild} from '@angular/core';
import {Mode} from '@shared/enums/mode.enum';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {NzModalRef} from 'ng-zorro-antd/modal';
import {NotificationService} from '@shared/services/notification.service';
import {OrderDetail, OrderModel} from '@modules/administration/orders/interfaces/order.model';
import {OrderService} from '@modules/administration/orders/services/order.service';
import * as _ from 'lodash';
import {BaseResponse} from '@core/models/base-response.model';
import {MESSAGE_CODE} from '@shared/constants/message-code.constant';
import {VALIDATORS} from './edit-order.validator';
import {DecimalPipe} from '@angular/common';
import {FormatCurrencyPipe} from '@shared/pipes/format-currency.pipe';
import {isCheckDisabled} from "ng-zorro-antd/core/tree";

@Component({
    selector: 'app-edit-order',
    templateUrl: './edit-order.component.html',
    styleUrls: ['./edit-order.component.css']
})
export class EditOrderComponent implements OnInit {
    @Input() data: OrderModel;
    @Input() mode: Mode = Mode.ADD;
    readonly MODE = Mode;
    readonly VALIDATORS = VALIDATORS;
    form: FormGroup;
    isSubmitted: boolean = false;
    readonly defaultTime = new Date(2000, 0, 1, 0, 0, 0);
    totalAmount = 0;
    shipFee = 0;
    listOrderDetail: OrderDetail[];
    @ViewChild('editFooter') editFooter: TemplateRef<any>;

    constructor(
        @Optional() private modalRef: NzModalRef,
        private fb: FormBuilder,
        private notiService: NotificationService,
        private orderService: OrderService,
        private formatCurrencyPipe: FormatCurrencyPipe
    ) {
        this.form = this.fb.group({
            id: null,
            customerName: [null],
            address: [null],
            phoneNumber: [null],
            product: [null],
            amount: [0],
            shipFee: [0],
            totalAmount: [0, [Validators.required]],
            channel: [null, [Validators.required]],
            channelPayment: [null, [Validators.required]],
            linkFb: [null],
            moreInfor: [null],
            paid: [0],
            orderDate: [null],
            orderDetailForm: this.fb.array([this.createItemOrderDetailForm(null, 1, null, null,null)]),
        });
    }

    get f() {
        return this.form.controls;
    }

    ngOnInit(): void {
        this.form.controls.totalAmount.disable({});
        this.patchData();
    }


    private patchData() {
        if (this.data) {
            const listOrd = this.form.get('orderDetailForm') as FormArray;
            this.listOrderDetail = this.parseString(this.data.product);
            if (this.listOrderDetail.length > 0) {
                listOrd.clear();
                this.listOrderDetail.forEach((item, index) => {
                    listOrd.push(this.createItemOrderDetailForm(item.productDetail, item.quantity, item.price, index,item.subTotal ));
                });
            }
            this.form.patchValue(this.data);
            this.form.controls.amount.setValue(this.formatCurrencyPipe.transform(this.data.amount, '', 'symbol', '1.0-0'));
            this.form.controls.totalAmount.setValue(this.formatCurrencyPipe.transform(this.data.totalAmount, '', 'symbol', '1.0-0'));
            this.form.controls.shipFee.setValue(this.formatCurrencyPipe.transform(this.data.shipFee, '', 'symbol', '1.0-0'));
        }
    }

    cancel() {
        this.modalRef?.destroy();
    }

    submitForm() {
        this.isSubmitted = true;
        for (const i in this.form.controls) {
            this.form.controls[i].markAsDirty();
            this.form.controls[i].updateValueAndValidity();
        }
        if (this.form.valid) {
            const orderDetailControls = this.form.get('orderDetailForm')['controls'];
            let listOrder = '';
            for (const orderDetail of orderDetailControls) {
                let productDetail = orderDetail.value.productDetail;
                let price = orderDetail.value.price ? orderDetail.value.price.toString() : '0';
                let quantity = orderDetail.value.quantity ? orderDetail.value.quantity.toString() : '0';
                listOrder += productDetail + '|' + quantity + '|' + price + '#';
            }

            const request = {
                id: this.f.id.value,
                customerName: this.f.customerName.value,
                address: this.f.address.value,
                phoneNumber: this.f.phoneNumber.value,
                product: listOrder,
                amount: this.formatCurrencyPipe.parse(this.f.amount.value),
                shipFee: this.formatCurrencyPipe.parse(this.f.shipFee.value),
                totalAmount: this.formatCurrencyPipe.parse(this.f.totalAmount.getRawValue()),
                channel: this.f.channel.value,
                channelPayment: this.f.channelPayment.value,
                linkFb: this.f.linkFb.value,
                moreInfor: this.f.moreInfor.value,
                paid: this.f.paid.value,
                orderDate: this.buildOrderDate(this.f.orderDate.value)
            };
            switch (this.mode) {
                case Mode.ADD:
                    this.add(request);
                    break;
                case  Mode.EDIT:
                    this.edit(request);
                    break;
            }
        }
    }

    ngAfterViewInit(): void {
        Promise.resolve().then(() => {
            this.modalRef?.updateConfig({
                nzFooter: this.editFooter
            });
        });
    }


    private add(request: any) {
        this.orderService.add(request).subscribe((res: BaseResponse<any, any>) => {
            if (res?.messageCode === MESSAGE_CODE.SUCCESS) {
                this.notiService.success(res.message);
                this.modalRef?.destroy({refresh: true});
            } else {
                this.notiService.error(BaseResponse.getMessage(res));
            }
            this.isSubmitted = false;

        }, error => {
            this.isSubmitted = false;
            this.notiService.error(BaseResponse.getMessage(error));
        });
    }

    private edit(request: any) {
        this.orderService.edit(request).subscribe((res: BaseResponse<any, any>) => {
            if (res?.messageCode === MESSAGE_CODE.SUCCESS) {
                this.notiService.success(res.message);
                this.modalRef?.destroy({refresh: true});
            } else {
                this.notiService.error(BaseResponse.getMessage(res));
            }
            this.isSubmitted = false;
        }, error => {
            this.isSubmitted = false;
            this.notiService.error(BaseResponse.getMessage(error));
        });
    }

    buildOrderDate(value: any): string | null {
        if (!value) return null;
        const d = new Date(value);
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
    }

    calculatorAmount() {
        const amount = this.formatCurrencyPipe.parse(this.form.controls.amount.value) || 0;
        const shipFeeRaw = this.form.controls.shipFee.value;
        const shipFee = this.formatCurrencyPipe.parse(
            typeof shipFeeRaw === 'string' ? shipFeeRaw : shipFeeRaw?.toString() || '0'
        );
        const amountNumber = Number(amount);
        const shipFeeNumber = Number(shipFee);
        this.totalAmount = amountNumber + shipFeeNumber;
        this.form.controls.totalAmount.setValue(this.formatCurrencyPipe.transform(this.totalAmount, '', 'symbol', '1.0-0'));
        this.form.controls.shipFee.setValue(this.formatCurrencyPipe.transform(shipFeeNumber, '', 'symbol', '1.0-0'));
    }

    createItemOrderDetailForm(productDetail: string, quantity: number, price: number, index: number,subTotal: number): FormGroup {
        return new FormGroup({
            productDetail: new FormControl(productDetail != null ? productDetail : null, Validators.required),
            quantity: new FormControl(quantity != null ? quantity : 1, Validators.required),
            price: new FormControl(price != null ? price : 0, Validators.required),
            subTotal: new FormControl(
                { value:this.formatCurrencyPipe.transform((quantity * price), '', 'symbol', '1.0-0') , disabled: true },
                Validators.required
            )
        });
    }

    parseString(input: string): OrderDetail[] {
        const regex = /([^|#]+)\|(\d+)\|(\d+)/g;
        const items: OrderDetail[] = [];
        let match: RegExpExecArray | null;

        while ((match = regex.exec(input)) !== null) {
            items.push({
                productDetail: match[1],
                quantity: parseInt(match[2], 10),
                price: parseInt(match[3], 10),
                subTotal: parseInt(match[2], 10) * parseInt(match[3], 10),
            });
        }

        return items;
    }

    addItemOrderDetailForm() {
        const orderDetail = this.form.get('orderDetailForm') as FormArray;
        orderDetail.push(this.createItemOrderDetailForm(null, null, null, null,null
        ));

    }

    removeItemOrderDetailForm(i: number) {
        const orderDetail = this.form.get('orderDetailForm') as FormArray;
        if (orderDetail.length > 1) {
            orderDetail.removeAt(i);
            this.onChangeCaculatorAmountOrderDetail(i);
            this.calculatorAmount();
        } else {
            orderDetail.reset();
        }
    }

    onChangeCaculatorAmountOrderDetail(index: number) {
        let amount = 0;
        const orderDetailControls = this.form.get('orderDetailForm')['controls'];

        for (const orderDetail of orderDetailControls) {
            let price = orderDetail.value.price ? orderDetail.value.price.toString() : '0';
            let quantity = orderDetail.value.quantity ? orderDetail.value.quantity.toString() : '0';

            // Remove any characters that are not digits or a decimal point
            price = price.replace(/[^\d.,]/g, '');
            quantity = quantity.replace(/[^\d.,]/g, '');

            const priceNumber = Number(price);
            const quantityNumber = Number(quantity);
            // Calculate the amount only if both price and quantity are valid numbers
            if (!isNaN(priceNumber) && !isNaN(quantityNumber)) {
                amount += priceNumber * quantityNumber;
                orderDetail.get('subTotal').setValue(this.formatCurrencyPipe.transform(priceNumber * quantityNumber, '', 'symbol', '1.0-0'))
            }

        }
        // Format the total amount and set it in the form control
        this.form.controls.amount.setValue(this.formatCurrencyPipe.transform(amount, '', 'symbol', '1.0-0'));
    }


}
